import axios from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  IAddCashRequest,
  ICLoseCashierRequest,
  IDayEndRequest,
  INewCashierRequest,
  IOpenCashierRequest,
  IRemoveCashRequest,
} from "../../typings/interfaces/requests/cashierStationRequests";
import {
  GET_ALL_STATIONS,
  GET_ALL_STATIONS_WITH_STATUS,
  GET_AVAILABLE_STATIONS_FOR_ONBOARDING,
  GET_STATION_BY_ID,
  GET_DENIED_STATIONS,
  GET_GRANTED_STATIONS,
  NEW_STATION,
  UPDATE_STATION_ACCESS,
  DEACTIVATE_STATION,
  ACTIVATE_STATION,
  OPEN_STATION_CASHIER,
  GET_STATION_CASHIER_SUMMARY,
  GET_STATION_SUMMARY,
  ADD_CASH,
  REMOVE_CASH,
  GET_STORE_SUMMARY,
  CLOSE_STATION_CASHIER,
  GET_ALL_STATION_SUMMARY,
  GET_ALL_STATIONS_DAY_END_SUMMARY,
  END_DAY,
  LATEST_CASHIER_SESSION,
  GET_ALL_CASHIER_POINTS,
  DENY_STATION_ACCESS,
  GET_REFUNDS_FOR_STATION,
  GET_STORE_CATEGORY,
  GET_REFUNDS_FOR_STORE,
  GET_CASHIER_INFO,
  STORE_STATUS_CHECK,
  GET_STATION_ACCESS_SESSIONS,
  ADMIN_CLOSE_STATION,
} from "../../configs";
import { BaseResponse, BaseResponseSingle } from "../../typings";
import {
  IAddCashResponse,
  ICashierDetailsResponse,
  ICashierPointResponse,
  ICategorySummary,
  ICloseCashierResponse,
  IDayEndStationSummaryResponse,
  IEndDayResponse,
  ILatestCashierSessionResponse,
  IOpenCashierResponse,
  IRefundResponse,
  IRemoveCashResponse,
  IStationCashierSummaryResponse,
  IStationSummaryResponse,
  IStoreSummaryResponse,
  storeStatus,
} from "../../typings/interfaces/responses/cashierStationResponses";
import { StationManagementDto } from "../../typings/interfaces/responses/stationManagementResponses";
import { IStationAccessListItem, IAdminCloseStationRequest } from "../../typings/interfaces/responses/stationAccessResponses";
import { store } from "../store";

export const requestNewStationAction = createAsyncThunk(
  "cashier-point/request-new",
  async (data: INewCashierRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post<
        BaseResponseSingle<ICashierPointResponse>
      >(NEW_STATION, data);
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const getAllStationsAction = createAsyncThunk(
  "cashier-point/get-all",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<ICashierPointResponse>>(
        GET_ALL_STATIONS
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getStoreStatusAction = createAsyncThunk(
  "cashier-point/get-store-status",
  async (storeId: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponseSingle<storeStatus>>(
        STORE_STATUS_CHECK(storeId)
      );

      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getAllCashierPointsActions = createAsyncThunk(
  "cashier-point/get-all/all",
  async (storeId: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<ICashierPointResponse>>(
        GET_ALL_CASHIER_POINTS(storeId)
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || "Unknown error occurred");
    }
  }
);

export const getAllGrantedStationsAction = createAsyncThunk(
  "cashier-point/get-granted",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<ICashierPointResponse>>(
        GET_GRANTED_STATIONS
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const getAllRejectedStationsAction = createAsyncThunk(
  "cashier-point/get-rejected",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<ICashierPointResponse>>(
        GET_DENIED_STATIONS
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const getStationByIdAction = createAsyncThunk(
  "cashier-point/get-by-id",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<
        BaseResponseSingle<ICashierPointResponse>
      >(GET_STATION_BY_ID(id));
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateStationAccessAction = createAsyncThunk(
  "cashier-point/update-access",
  async (
    {
      id,
    }: {
      id: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.patch<
        BaseResponseSingle<ICashierPointResponse>
      >(UPDATE_STATION_ACCESS(id));
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const denyStationAccessAction = createAsyncThunk(
  "cashier-point/deny-access",
  async (
    {
      id,
    }: {
      id: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.patch<
        BaseResponseSingle<ICashierPointResponse>
      >(DENY_STATION_ACCESS(id));
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const openStationCashierAction = createAsyncThunk(
  "cashier-point/open",
  async (data: IOpenCashierRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post<
        BaseResponseSingle<IOpenCashierResponse>
      >(OPEN_STATION_CASHIER, data);

      const result = response.data.body;

      localStorage.setItem(
        "stationAccessSessionId",
        result.stationAccessSessionId.toString()
      );
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getStationCashierSummaryAction = createAsyncThunk(
  "cashier-point/cashier-summary",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<
        BaseResponse<IStationCashierSummaryResponse>
      >(GET_STATION_CASHIER_SUMMARY(id));
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const getStationSummaryAction = createAsyncThunk(
  "cashier-point/station-summary",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<
        BaseResponseSingle<IStationSummaryResponse>
      >(GET_STATION_SUMMARY(id));

      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const addCashAction = createAsyncThunk(
  "cashier-point/add-cash",
  async (data: IAddCashRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post<BaseResponse<IAddCashResponse>>(
        ADD_CASH,
        data
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const removeCashAction = createAsyncThunk(
  "cashier-point/remove-cash",
  async (data: IRemoveCashRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post<BaseResponse<IRemoveCashResponse>>(
        REMOVE_CASH,
        data
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getStoreSummaryAction = createAsyncThunk(
  "cashier-point/store-summary",
  async (storeId: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<
        BaseResponseSingle<IStoreSummaryResponse>
      >(GET_STORE_SUMMARY(storeId));
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const closeStationCashierAction = createAsyncThunk(
  "cashier-point/close",
  async (data: ICLoseCashierRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post<
        BaseResponseSingle<ICloseCashierResponse>
      >(CLOSE_STATION_CASHIER, data);
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getAllStationSummaryAction = createAsyncThunk(
  "cashier-point/all-station-summary",
  async (storeId: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<IStationSummaryResponse>>(
        GET_ALL_STATION_SUMMARY(storeId)
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getAllDayEndStationSummaryAction = createAsyncThunk(
  "cashier-point/day-end-station-summary",
  async (
    { storeId, date }: { storeId: number; date?: Date },
    { rejectWithValue }
  ) => {
    try {
      // date format: yyyy-mm-dd
      const formattedDate = date ? date.toISOString().split("T")[0] : undefined;

      const response = await axios.get<
        BaseResponse<IDayEndStationSummaryResponse>
      >(GET_ALL_STATIONS_DAY_END_SUMMARY(storeId, formattedDate));

      return response.data.body;
    } catch (error: any) {
      // Extract error message from various possible locations
      let errorMessage = "An error occurred";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        // Check for message field (even if empty, try error field)
        if (errorData.message && errorData.message.trim() !== "") {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          // If message is empty, use error field and status if available
          const status = errorData.status ? ` (${errorData.status})` : "";
          errorMessage = `${errorData.error}${status}`;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const endDayAction = createAsyncThunk(
  "cashier-point/end-day",
  async (data: IDayEndRequest, { rejectWithValue }) => {
    console.log("End Day Action Data:", data);
    try {
      const response = await axios.post<BaseResponseSingle<IEndDayResponse>>(
        END_DAY,
        data
      );

      return response.data.body;
    } catch (error: any) {
      // Extract error message from various possible locations
      let errorMessage = "An error occurred";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        // Check for message field (even if empty, try error field)
        if (errorData.message && errorData.message.trim() !== "") {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          // If message is empty, use error field and status if available
          const status = errorData.status ? ` (${errorData.status})` : "";
          errorMessage = `${errorData.error}${status}`;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const getCashierLatestSessionAction = createAsyncThunk(
  "cashier-point/latest-session",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<
        BaseResponseSingle<ILatestCashierSessionResponse>
      >(LATEST_CASHIER_SESSION(id));
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getRefundsForStationAction = createAsyncThunk(
  "cashier-point/get-refunds-for-station",
  async (stationId: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<IRefundResponse>>(
        GET_REFUNDS_FOR_STATION(stationId)
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch refunds");
    }
  }
);

export const getRefundsForStoreAction = createAsyncThunk(
  "cashier-point/get-refunds-for-store",
  async (storeId: number, { rejectWithValue }) => {
    console.log("Fetching refunds for station ID:", storeId);
    try {
      const response = await axios.get<BaseResponse<IRefundResponse>>(
        GET_REFUNDS_FOR_STORE(storeId)
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch refunds");
    }
  }
);

export const getCashierDetailsAction = createAsyncThunk(
  "cashier-point/get-cashier-details",
  async (deviceId: string, { rejectWithValue }) => {
    try {
      const response = await axios.get<
        BaseResponseSingle<ICashierDetailsResponse>
      >(GET_CASHIER_INFO(deviceId));
      if (response.data.body) {
        localStorage.setItem("cashier", JSON.stringify(response.data.body));
        if (response.data.body.activeSessionId) {
          localStorage.setItem(
            "stationAccessSessionId",
            JSON.stringify(response.data.body.activeSessionId)
          );
        }
      }
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch cashier details"
      );
    }
  }
);

export const getStoreCategorySummaryAction = createAsyncThunk(
  "cashier-point/store-category-summary",
  async (storeId: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<ICategorySummary>>(
        GET_STORE_CATEGORY(storeId)
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch category summary"
      );
    }
  }
);

// Add new actions for enhanced station management
export const getAllStationsWithStatusAction = createAsyncThunk(
  "cashier-point/get-all-with-status",
  async (storeId: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<StationManagementDto>>(
        GET_ALL_STATIONS_WITH_STATUS(storeId)
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || "Unknown error occurred");
    }
  }
);

export const getAvailableStationsForOnboardingAction = createAsyncThunk(
  "cashier-point/get-available-for-onboarding",
  async (storeId: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<ICashierPointResponse>>(
        GET_AVAILABLE_STATIONS_FOR_ONBOARDING(storeId)
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || "Unknown error occurred");
    }
  }
);

// Station activation/deactivation actions
export const deactivateStationAction = createAsyncThunk(
  "cashier-point/deactivate-station",
  async (stationId: number, { rejectWithValue }) => {
    try {
      const response = await axios.patch<BaseResponseSingle<ICashierPointResponse>>(
        DEACTIVATE_STATION(stationId)
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || "Failed to deactivate station");
    }
  }
);

export const activateStationAction = createAsyncThunk(
  "cashier-point/activate-station",
  async (stationId: number, { rejectWithValue }) => {
    try {
      const response = await axios.patch<BaseResponseSingle<ICashierPointResponse>>(
        ACTIVATE_STATION(stationId)
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || "Failed to activate station");
    }
  }
);

export const getStationAccessSessionsAction = createAsyncThunk(
  "cashier-station/get-access-sessions",
  async (
    params: { storeId: number; date?: string; status?: string },
    { rejectWithValue }
  ) => {
    try {
      const { storeId, date, status } = params;
      const url = GET_STATION_ACCESS_SESSIONS(storeId);
      const queryParams = new URLSearchParams();
      if (date) queryParams.append("date", date);
      if (status) queryParams.append("status", status);
      
      const response = await axios.get<BaseResponse<IStationAccessListItem>>(
        `${url}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to fetch station access sessions"
      );
    }
  }
);

export const adminCloseStationAction = createAsyncThunk(
  "cashier-station/admin-close-station",
  async (data: IAdminCloseStationRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post<BaseResponseSingle<ICloseCashierResponse>>(
        ADMIN_CLOSE_STATION,
        data
      );
      return response.data.body;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to close station"
      );
    }
  }
);
